import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import Button from '../ui/Button';
import { 
  FiMessageSquare, 
  FiGlobe, 
  FiLayout, 
  FiTrendingUp, 
  FiLink, 
  FiSettings,
  FiMenu,
  FiX,
  FiLogOut,
  FiStar,
  FiBarChart2,
  FiUsers
} from 'react-icons/fi';

const SidebarItem = ({ icon, label, to, active }) => (
  <Link 
    to={to} 
    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
      active 
        ? 'bg-gradient-to-r from-orange-500/10 to-transparent text-amber-100 border-l-2 border-orange-500' 
        : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'
    }`}
  >
    <SafeIcon icon={icon} className={`text-lg transition-colors duration-300 ${active ? 'text-orange-400' : ''}`} />
    <span className="font-medium text-sm">{label}</span>
  </Link>
);

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [headerAction, setHeaderAction] = useState(null); // Added state for dynamic header actions
  
  const location = useLocation();
  const navigate = useNavigate();
  const { user, handleCheckout, signOut } = useAuth();
  const currentPath = location.pathname.split('/').pop() || 'reviews';
  
  // Profile state for Sidebar
  const [profile, setProfile] = useState({ full_name: '', avatar_url: '' });

  // Close sidebar on route change for mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  // Fetch strictly the logged-in user's profile metadata for the sidebar
  useEffect(() => {
    if (!user?.id) return;

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, avatar_url')
          .eq('id', user.id)
          .single();
          
        if (!error && data) {
          setProfile(data);
        }
      } catch (err) {
        console.error('Error fetching sidebar profile:', err);
      }
    };

    fetchProfile();

    // Subscribe to profile changes to keep sidebar reactive
    const channel = supabase
      .channel('profile_changes')
      .on(
        'postgres',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
        (payload) => {
          setProfile(prev => ({ ...prev, ...payload.new }));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const navItems = [
    { id: 'reviews', icon: FiMessageSquare, label: 'Reviews' },
    { id: 'my-page', icon: FiGlobe, label: 'My Page' },
    { id: 'widgets', icon: FiLayout, label: 'Widgets' },
    { id: 'seo', icon: FiTrendingUp, label: 'SEO' },
    { id: 'connect', icon: FiLink, label: 'Connect' },
  ];

  const headerContent = {
    'reviews': { title: 'Review Management', desc: 'Curate, moderate, and publish your premium customer testimonials.' },
    'widgets': { title: 'Widget Studio', desc: 'Design and embed stunning, high-converting social proof widgets.' },
    'connect': { title: 'Integrations & Sync', desc: 'Connect your data sources to automatically pull in the latest testimonials.' },
    'seo': { title: 'Page Branding', desc: 'Configure your company identity and SEO metadata for your hosted Wall of Love.' },
    'my-page': { title: 'My Public Page', desc: 'Share your dedicated Wall of Love with your prospects and customers.' },
    'reports': { title: 'Revenue Reports', desc: 'Financial overview and lifetime value for premium users.' },
    'users': { title: 'User Management', desc: 'View, edit, and manage all platform users.' }
  };

  const currentHeaderInfo = headerContent[currentPath];

  // Fallback for user display
  const displayFullName = profile.full_name || 'User';
  const initial = profile.full_name 
    ? profile.full_name.charAt(0).toUpperCase() 
    : (user?.email?.charAt(0).toUpperCase() || 'U');

  return (
    <div className="flex h-screen bg-[#0a0500] overflow-hidden selection:bg-orange-500/30 w-full">
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm md:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } border-r border-orange-500/10 bg-[#0a0500]/95 md:bg-[#0a0500]/50 backdrop-blur-xl flex flex-col h-full flex-shrink-0`}
      >
        <div className="h-20 flex items-center justify-between px-6 border-b border-orange-500/10">
          <Link to="/" className="flex items-center gap-3 group transition-all duration-300 hover:opacity-80">
            <img 
              src="https://whatastory.b-cdn.net/tools/momentoftruth/logo.svg" 
              alt="Moment of Truth Logo" 
              className="h-8 w-auto drop-shadow-[0_0_15px_rgba(234,88,12,0.3)] group-hover:drop-shadow-[0_0_25px_rgba(234,88,12,0.6)] transition-all duration-300" 
            />
            <span className="text-lg font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-amber-100 via-orange-300 to-orange-500">
              Moment of Truth
            </span>
          </Link>
          <button 
            className="md:hidden text-zinc-400 hover:text-amber-100 p-1"
            onClick={() => setSidebarOpen(false)}
          >
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <div className="flex-1 py-6 px-3 flex flex-col gap-1 overflow-y-auto">
          <div className="px-3 mb-2 text-xs font-semibold tracking-wider text-zinc-600 uppercase">Menu</div>
          {navItems.map(item => (
            <SidebarItem 
              key={item.id}
              icon={item.icon}
              label={item.label}
              to={`/dashboard/${item.id}`}
              active={currentPath === item.id}
            />
          ))}

          {/* Superadmin Menu Section */}
          {user?.role === 'superadmin' && (
            <div className="mt-6">
              <div className="px-3 mb-2 text-xs font-semibold tracking-wider text-orange-500/70 uppercase">Admin</div>
              <SidebarItem 
                icon={FiUsers}
                label="Users"
                to={`/dashboard/users`}
                active={currentPath === 'users'}
              />
              <SidebarItem 
                icon={FiBarChart2}
                label="Reports"
                to={`/dashboard/reports`}
                active={currentPath === 'reports'}
              />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-orange-500/10 flex flex-col gap-2">
          {/* Subtle "Get Pro" Button mapped above settings if user is not pro */}
          {!user?.is_pro && (
            <Link 
              onClick={() => handleCheckout('monthly')}
              className="group relative flex items-center justify-between px-4 py-2.5 rounded-xl bg-gradient-to-r from-orange-600/10 to-yellow-500/10 border border-orange-500/20 hover:border-orange-500/40 transition-all duration-300 overflow-hidden mb-1"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600/0 to-yellow-500/0 group-hover:from-orange-600/10 group-hover:to-yellow-500/10 transition-all duration-300"></div>
              <div className="flex items-center gap-2.5 relative z-10">
                <SafeIcon icon={FiStar} className="text-yellow-400 text-sm drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]" />
                <span className="text-xs font-bold tracking-wide text-amber-100/90 group-hover:text-amber-50">Go Premium</span>
              </div>
              <span className="text-[10px] text-orange-400/90 font-bold tracking-wide px-2 py-0.5 rounded-md bg-orange-500/10 border border-orange-500/20 relative z-10 shadow-sm">
                $21/mo
              </span>
            </Link>
          )}

          {/* User Avatar & Settings Link */}
          <Link 
            to="/dashboard/settings" 
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-transparent hover:border-orange-500/20 bg-transparent hover:bg-zinc-900/50 transition-all duration-300 group"
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0 overflow-hidden group-hover:shadow-[0_0_15px_rgba(234,88,12,0.3)] transition-all duration-300">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-amber-100 font-semibold text-sm">{initial}</span>
              )}
            </div>
            <div className="overflow-hidden flex-1">
              <div className="text-sm font-medium text-zinc-300 group-hover:text-amber-50 transition-colors duration-300 flex items-center gap-2">
                <span className="truncate">{displayFullName}</span>
                {user?.is_pro && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-yellow-400 to-amber-600 text-black shadow-[0_0_10px_rgba(251,191,36,0.3)] tracking-wider flex-shrink-0">
                    PRO
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                <SafeIcon icon={FiSettings} className="text-[10px]" /> Settings
              </div>
            </div>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-zinc-950 flex flex-col w-full relative min-w-0">
        <header className="h-20 flex-shrink-0 flex items-center justify-between px-4 md:px-8 border-b border-orange-500/5 bg-[#0a0500]/30 backdrop-blur sticky top-0 z-30">
          <div className="flex items-center min-w-0">
            <button 
              className="md:hidden mr-4 text-zinc-400 hover:text-amber-100 transition-colors p-2 -ml-2"
              onClick={() => setSidebarOpen(true)}
            >
              <SafeIcon icon={FiMenu} className="text-2xl" />
            </button>
            {currentHeaderInfo ? (
              <div className="flex flex-col truncate">
                <h2 className="text-xl font-bold tracking-tight text-amber-50 truncate">{currentHeaderInfo.title}</h2>
                <p className="text-xs text-zinc-400 truncate hidden sm:block mt-0.5">{currentHeaderInfo.desc}</p>
              </div>
            ) : (
              <h1 className="text-xl font-semibold text-amber-50 capitalize truncate">{currentPath.replace('-', ' ')}</h1>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* Dynamic Header Actions (Injected by Pages) */}
            {headerAction}

            {currentPath === 'settings' && (
              <Button 
                variant="ghost" 
                onClick={handleLogout}
                className="!text-red-400 hover:!text-red-300 hover:!bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all !py-2 !px-4 flex-shrink-0"
              >
                <SafeIcon icon={FiLogOut} />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            )}
          </div>
        </header>

        <div className="p-4 md:p-8 flex-1 overflow-x-hidden">
          {/* Pass setHeaderAction down to child routes */}
          <Outlet context={{ setHeaderAction }} />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;